namespace $ {
	export type $easyreels_i18n_language = 'ru' | 'en'

	export type $easyreels_i18n_params = Record<string, string | number>

	export type $easyreels_i18n_note = {
		text: () => string
		detail: string
	}

	export class $easyreels_i18n_error extends Error {
		constructor(message: string, public note: $easyreels_i18n_note) {
			super(message)
		}
	}

	const units = ['b', 'kb', 'mb', 'gb', 'tb']
	const statuses = ['PENDING', 'ASSEMBLING', 'READY', 'FAILED']
	const kinds = ['RECORD', 'UPLOAD']

	const known: Record<string, string> = {
		'the recording limit is reached: nothing was issued': 'quota',
		'recording not found': 'not_found',
		'the upload no longer accepts parts': 'not_accepting',
		'the recording no longer accepts uploads': 'not_accepting',
		'the recording no longer accepts a manifest': 'not_accepting',
		'the upload window of this recording has passed': 'window_expired',
		'upload window expired': 'window_expired',
		'the file needs a name of at most 255 bytes': 'file_name',
		'only a video file can be uploaded': 'file_mime',
		'the file size must be positive': 'file_bytes',
		'the object store refused to complete the upload: re-send the parts and complete again': 'completion_refused',
		'the uploaded file is missing': 'upload_missing',
		'the uploaded file is not the size startFileUpload declared': 'upload_size',
		'wrong email or password': 'wrong_pair',
		'required': 'required',
		'a label is required': 'label_required',
		'a label is at most 100 characters': 'label_long',
		'you already have a key with this label': 'label_taken',
		'api key not found': 'key_not_found',
	}

	const streams_missing = 'it needs both a video and an audio stream'

	export function $easyreels_i18n_lang(next?: $easyreels_i18n_language): $easyreels_i18n_language {
		if (next) $mol_locale.lang(next)

		return $mol_locale.lang() === 'ru' ? 'ru' : 'en'
	}

	export function $easyreels_i18n_text(key: string, params: $easyreels_i18n_params = {}) {
		let text = $mol_locale.text(key)

		for (const name of Object.keys(params)) text = text.split(`{${name}}`).join(String(params[name]))

		return text
	}

	export function $easyreels_i18n_say(key: string, params?: () => $easyreels_i18n_params, detail = ''): $easyreels_i18n_note {
		return { text: () => $easyreels_i18n_text(key, params?.()), detail }
	}

	export function $easyreels_i18n_bytes(bytes: number) {
		let value = bytes
		let unit = 0

		while (value >= 1024 && unit < units.length - 1) {
			value /= 1024
			unit++
		}

		const locale = $easyreels_i18n_lang() === 'ru' ? 'ru-RU' : 'en-US'
		const text = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, useGrouping: false }).format(value)

		return text + ' ' + $easyreels_i18n_text(`$easyreels_i18n_unit_${units[unit]}`)
	}

	export function $easyreels_i18n_seconds(seconds: number, digits = 0) {
		const locale = $easyreels_i18n_lang() === 'ru' ? 'ru-RU' : 'en-US'
		const text = new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(seconds)

		return text + ' ' + $easyreels_i18n_text('$easyreels_i18n_unit_s')
	}

	export function $easyreels_i18n_status(status: string) {
		return statuses.includes(status) ? $easyreels_i18n_text(`$easyreels_i18n_status_${status}`) : status
	}

	export function $easyreels_i18n_kind(kind: string) {
		return kinds.includes(kind) ? $easyreels_i18n_text(`$easyreels_i18n_kind_${kind}`) : kind
	}

	export function $easyreels_i18n_layout(seconds = false) {
		return $easyreels_i18n_text(seconds ? '$easyreels_i18n_layout_seconds' : '$easyreels_i18n_layout')
	}

	export function $easyreels_i18n_server(message: string): $easyreels_i18n_note {
		const key = known[message] ?? (message.endsWith(streams_missing) ? 'streams_missing' : '')

		if (key) return $easyreels_i18n_say(`$easyreels_i18n_server_${key}`)

		return $easyreels_i18n_say('$easyreels_i18n_server_unknown', undefined, message)
	}

	export function $easyreels_i18n_failure(error: unknown): $easyreels_i18n_note {
		const message = error instanceof Error ? error.message : String(error)

		return $easyreels_i18n_say('$easyreels_i18n_failure', undefined, message)
	}

	export function $easyreels_i18n_error_note(error: unknown) {
		return error instanceof $easyreels_i18n_error ? error.note : $easyreels_i18n_failure(error)
	}

	export function $easyreels_i18n_because(key: string, error: unknown): $easyreels_i18n_note {
		if (error instanceof $easyreels_i18n_error) {
			const inner = error.note

			return { text: () => `${$easyreels_i18n_text(key)}: ${inner.text()}`, detail: inner.detail }
		}

		return $easyreels_i18n_say(key, undefined, error instanceof Error ? error.message : String(error))
	}

	export function $easyreels_i18n_plural(key: string, count: number, params: $easyreels_i18n_params = {}) {
		const locale = $easyreels_i18n_lang() === 'ru' ? 'ru-RU' : 'en-US'
		const category = new Intl.PluralRules(locale).select(count)

		return $easyreels_i18n_text(`${key}_${category}`, { ...params, count })
	}

	export function $easyreels_i18n_note_line(note: $easyreels_i18n_note | null) {
		if (!note) return ''

		return note.detail ? `${note.text()}: ${note.detail}` : note.text()
	}
}
